/**
 * Surfaces training metadata for an ML model so users can judge freshness
 * + headline quality. Placed above charts/tables that consume the model.
 */

import React from 'react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { useModelInfoByName } from '@/hooks/ml';

interface Props {
  modelName: string;
  className?: string;
}

const HEADLINE_METRIC_BY_MODEL: Record<string, string> = {
  reply_rate_predictor: 'roc_auc',
  send_timing: 'c_index',
  ecm_enrollment_predictor: 'roc_auc',
};

function formatMetric(name: string, value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  if (name === 'top_decile_lift') return `${value.toFixed(2)}×`;
  return value.toFixed(3);
}

export const ModelMetadataBanner: React.FC<Props> = ({ modelName, className = '' }) => {
  const { data, isLoading, isError } = useModelInfoByName(modelName);

  if (isLoading) {
    return (
      <div className={`text-xs text-gray-500 ${className}`}>
        Loading model metadata…
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className={`text-xs text-gray-500 ${className}`}>
        Model metadata unavailable
      </div>
    );
  }

  const headline = HEADLINE_METRIC_BY_MODEL[modelName];
  const headlineValue = headline && data.metrics ? data.metrics[headline] : null;
  const trainedAgo = data.trained_at
    ? formatDistanceToNow(parseISO(data.trained_at), { addSuffix: true })
    : 'unknown';

  return (
    <div
      className={`flex flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-700 ${className}`}
    >
      <span className="font-medium">Model:</span>
      <span className="font-mono">{data.name}</span>
      <span className="text-gray-400">·</span>
      <span>Trained {trainedAgo}</span>
      {headline && (
        <>
          <span className="text-gray-400">·</span>
          <span>
            {headline.replace('_', ' ')}: <span className="tabular-nums">{formatMetric(headline, headlineValue)}</span>
          </span>
        </>
      )}
      {typeof data.rows_held_out === 'number' && data.rows_held_out > 0 && (
        <>
          <span className="text-gray-400">·</span>
          <span>Holdout: {data.rows_held_out.toLocaleString()} rows</span>
        </>
      )}
    </div>
  );
};
